import { Component, computed, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import {
  YdsButtonComponent,
  YdsCardComponent,
  YdsEmptyStatesComponent,
  YdsSpinnerComponent,
  YdsTableComponent,
  YdsSplitButtonComponent,
  YdsTableCellDirective,
  YdsPaginationComponent,
  type YdsTableColumn,
  type YdsSplitButtonItem,
} from '../../../yds-angular';
import { formatDisplayDate } from '../../../shared/general-ledger-yds.utils';
import { ConfirmDialogService } from '../../../dialog/confirm-dialog/confirm-dialog.service';
import {
  GeneralledgerService,
  GeneralLedgerListItem,
} from '../generalledger.service';
import { deleteGeneralLedgerWithChecks } from '../generalledger-shared';

@Component({
  selector: 'app-generalledger-list',
  imports: [
    YdsCardComponent,
    YdsButtonComponent,
    YdsTableComponent,
    YdsSpinnerComponent,
    YdsEmptyStatesComponent,
    YdsSplitButtonComponent,
    YdsTableCellDirective,
    YdsPaginationComponent,
  ],
  templateUrl: './generalledger-list.component.html',
  styleUrl: './generalledger-list.component.scss',
})
export class GeneralledgerListComponent implements OnInit {
  readonly pageSize = 10;
  readonly tableColumns: YdsTableColumn[] = [
    { key: 'generalLedger_name', header: 'Naam' },
    { key: 'startDate', header: 'Begindatum' },
    { key: 'endDate', header: 'Einddatum' },
    { key: 'actions', header: 'Actieknop', width: '160px' },
  ];

  readonly splitButtonItems: readonly YdsSplitButtonItem[] = [
    { id: 'edit', label: 'Wijzigen' },
    { id: 'account', label: 'Grootboekrekening' },
    { id: 'accountgroup', label: 'Grootboekrekeningengroep' },
    { id: 'import', label: 'Importeren grootboek', disabled: true },
    { id: 'copy', label: 'Kopiëren grootboek', disabled: true },
    { id: 'delete', label: 'Verwijderen' },
  ];

  private router = inject(Router);
  private generalLedgerService = inject(GeneralledgerService);
  private dialogService = inject(ConfirmDialogService);
  private destroyRef = inject(DestroyRef);

  generalLedgers: WritableSignal<GeneralLedgerListItem[]> = signal([]);
  pageIndex = signal(0);
  isLoading = signal(true);

  private reload$ = new Subject<void>();

  pagedLedgers = computed(() => {
    const start = this.pageIndex() * this.pageSize;
    return this.generalLedgers().slice(start, start + this.pageSize);
  });

  tableItems = computed(() =>
    this.pagedLedgers().map((item) => ({
      id: item.generalLedgerId,
      generalLedger_name: item.generalLedger_name,
      startDate: formatDisplayDate(item.startDate, 'nl-NL'),
      endDate: this.formatEndDate(item.endDate),
    })),
  );

  private formatEndDate(endDate: GeneralLedgerListItem['endDate']): string {
    const formatted = formatDisplayDate(endDate, 'nl-NL');
    return formatted === '31-12-9999' ? 'n.v.t' : formatted;
  }

  constructor() {
    this.reload$
      .pipe(
        switchMap(() => {
          this.isLoading.set(true);
          this.generalLedgers.set([]);
          return this.generalLedgerService.getByContext().pipe(
            catchError(() => {
              this.isLoading.set(false);
              this.generalLedgers.set([]);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (items) => {
          this.generalLedgers.set(items);
          this.pageIndex.set(0);
          this.isLoading.set(false);
        },
      });
  }

  ngOnInit(): void {
    this.loadGeneralLedgers();
  }

  loadGeneralLedgers(): void {
    this.reload$.next();
  }

  navigateToCreate(): void {
    this.router.navigate(['/config/generalledger/editable']);
  }

  onTableRowClick(row: Record<string, unknown>): void {
    // Default action when clicking a row is "Bekijk details".
    this.viewItem(String(row['id']));
  }

  viewItem(id: string, event?: MouseEvent): void {
    event?.stopPropagation();
    this.router.navigate(['/config/generalledger/editable', id], {
      queryParams: { mode: 'view' },
    });
  }

  handleActionClick(id: string, actionId: string): void {
    if (actionId === 'edit') {
      this.router.navigate(['/config/generalledger/editable', id]);
      return;
    }
    if (actionId === 'account') {
      this.router.navigate(['/config/generalledgeraccount', id]);
      return;
    }
    if (actionId === 'accountgroup') {
      this.router.navigate(['/config/generalledgergroup']);
      return;
    }
    if (actionId === 'delete') {
      this.deleteItem(id);
      return;
    }
  }

  private deleteItem(id: string): void {
    deleteGeneralLedgerWithChecks({
      id,
      service: this.generalLedgerService,
      dialogService: this.dialogService,
      destroyRef: this.destroyRef,
      onDeleted: () => this.loadGeneralLedgers(),
    });
  }
}
